import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'

import { API_URL } from '@/helpers/api-url.helper'
import router from '@/router'

type ApiRequestConfig = AxiosRequestConfig & {
  preserveEmptyParams?: string[]
}

const publicApi: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
})

const privateApi: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
})

const loginApi: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
})

// El Hub no emite refresh token: el login devuelve solo `access_token` y el
// backend expone `/auth/login` y `/auth/me`, nada más. Así que un 401 no se
// renueva, se cierra la sesión.
let isClosingSession = false

/**
 * Cierra la sesión pasando por el store.
 *
 * `logout()` es el único camino que corta los websockets SIP
 * (`resetWebphoneConnection`). Limpiando solo `localStorage` el webphone seguía
 * registrado, la extensión quedaba ocupada en Asterisk y el siguiente login la
 * encontraba en uso.
 *
 * El import es dinámico porque el store depende de este cliente: estáticamente
 * sería un ciclo.
 */
const closeSessionOnUnauthorized = async () => {
  if (isClosingSession) return
  isClosingSession = true

  try {
    const { useAuthStore } = await import('@/stores/useAuthStore')
    await useAuthStore().logout()
  } catch {
    // Sin store disponible, al menos no dejar credenciales que parezcan válidas.
    localStorage.removeItem('tokenHub')
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    router.push('/auth/sign-in')
  } finally {
    isClosingSession = false
  }
}

privateApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('tokenHub')

    if (!token) {
      return Promise.reject(new axios.CanceledError('Missing auth token'))
    }

    config.headers.Authorization = `Bearer ${token}`

    return config
  },
  (error) => Promise.reject(error),
)

privateApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      const { status, data } = error.response
      const isTokenError = status === 401 || (data?.code && ['1008', '1009'].includes(data.code))

      if (isTokenError) {
        await closeSessionOnUnauthorized()
      }
    }

    return Promise.reject(error)
  },
)

const useApiClient = (isAuthenticated = false) => {
  const api = isAuthenticated ? privateApi : publicApi
  const apiLogin = loginApi

  const filterEmptyParams = (config?: ApiRequestConfig): AxiosRequestConfig | undefined => {
    if (!config?.params) return config

    const filteredParams: Record<string, unknown> = {}
    const preserveEmptyParams = new Set(config.preserveEmptyParams ?? [])

    Object.entries(config.params).forEach(([key, value]) => {
      if (preserveEmptyParams.has(key) && value === '') {
        filteredParams[key] = value
        return
      }

      if (value !== null && value !== undefined && value !== '') {
        if (typeof value === 'string') {
          const trimmedValue = value.trim()
          if (trimmedValue) {
            filteredParams[key] = trimmedValue
          }
        } else {
          filteredParams[key] = value
        }
      }
    })

    const { preserveEmptyParams: _preserveEmptyParams, ...axiosConfig } = config

    return {
      ...axiosConfig,
      params: filteredParams,
    }
  }

  return {
    get: async <T>(url: string, config?: ApiRequestConfig): Promise<T> => {
      const filteredConfig = filterEmptyParams(config)
      const response: AxiosResponse<T> = await api.get(url, filteredConfig)
      return response.data
    },

    post: async <T, R = unknown>(
      url: string,
      data?: R,
      config?: AxiosRequestConfig,
    ): Promise<T> => {
      const response: AxiosResponse<T> = await api.post(url, data, config)
      return response.data
    },

    put: async <T, R = unknown>(url: string, data?: R, config?: AxiosRequestConfig): Promise<T> => {
      const response: AxiosResponse<T> = await api.put(url, data, config)
      return response.data
    },

    patch: async <T, R = unknown>(
      url: string,
      data?: R,
      config?: AxiosRequestConfig,
    ): Promise<T> => {
      const response: AxiosResponse<T> = await api.patch(url, data, config)
      return response.data
    },

    delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
      const response: AxiosResponse<T> = await api.delete(url, config)
      return response.data
    },
    login: async <T, R = unknown>(
      url: string,
      data?: R,
      config?: AxiosRequestConfig,
    ): Promise<T> => {
      const response: AxiosResponse<T> = await apiLogin.post(url, data, config)
      return response.data
    },
  }
}

export { useApiClient }
